import * as React from 'react';

import { useViewer, type IDataColumn } from '@/design';
import { readViewerChoice, writeViewerChoice } from '@/design/viewer-store';
import type { IListFieldCatalogue, IListRow } from '../../../api/lists-contracts';
import { ColumnsSheet, type ColumnIcon } from '../columns-sheet';
import { extraColumns, type IWorkScope } from './controls';
import type { IWorkPage } from './use-work-page';

/**
 * A work page's columns, the viewer's (SR-456): one *Columns* sheet — Lists' editor — over the
 * page's own columns and any catalogue field beside them, in the viewer's order, kept on this
 * browser per page, survey and viewer as the design table's picker keeps its choice
 * (`veritant.columns.<id>.<viewer>`). Nothing stored, the page's own defaults draw: its columns in
 * their order, an optional one hidden where the page hides it. A field the catalogue no longer
 * offers is dropped as it is read; a page column the choice never knew draws at its default.
 */

export interface IColumnChoice {
  /** The columns shown, in order — a page column's id or a catalogue field's code. */
  order: string[];
  /** The page's columns when the choice was made: one added since draws at its default. */
  known: string[];
}

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((one): one is string => typeof one === 'string') : [];

function readChoice(key: string): Nullable<IColumnChoice> {
  const stored = readViewerChoice(key);
  return Array.isArray(stored.order) ? { order: strings(stored.order), known: strings(stored.known) } : null;
}

const labelOf = <Row,>(column: IDataColumn<Row>) =>
  column.label ?? (typeof column.header === 'string' ? column.header : column.id);

/** Drawn unless the page hides it until asked for. */
const byDefault = <Row,>(column: IDataColumn<Row>) => !(column.optional && column.hidden);

/** Choices made this page load, by key — read before the store, which can refuse a write. */
const chosenThisLoad = new Map<string, IColumnChoice>();

/** The viewer's choice under `veritant.columns.<id>.<viewer>`, and its writer. */
function useColumnChoice(id: string): [Nullable<IColumnChoice>, (made: IColumnChoice) => void] {
  const viewer = useViewer();
  const key = `veritant.columns.${id}.${viewer}`;
  // The stored choice is the key's from the render the key changes in — a survey switched never
  // reads under the last survey's columns.
  const stored = React.useMemo(() => chosenThisLoad.get(key) ?? readChoice(key), [key]);
  const [made, setMade] = React.useState<Nullable<{ key: string; choice: IColumnChoice }>>(null);
  const save = (choice: IColumnChoice) => {
    setMade({ key, choice });
    chosenThisLoad.set(key, choice);
    writeViewerChoice(key, choice);
  };
  return [made?.key === key ? made.choice : stored, save];
}

/** The ids drawn, in order: the page's defaults with no choice; else the choice's columns still
 *  offered (`kept`), then any page column added since at its default. */
function orderOf<Row>(
  choice: Nullable<IColumnChoice>,
  own: readonly IDataColumn<Row>[],
  kept: (code: string) => boolean = () => false
): string[] {
  if (!choice) return own.filter(byDefault).map((column) => column.id);
  const ids = new Set(own.map((column) => column.id));
  const shown = choice.order.filter((code) => ids.has(code) || kept(code));
  const added = own.filter((column) => !choice.known.includes(column.id) && byDefault(column));
  return [...shown, ...added.map((column) => column.id).filter((code) => !shown.includes(code))];
}

export interface IWorkColumns {
  /** The catalogue fields the viewer added — what the rows read asks beside the page's own. */
  extras: string[];
  /** The page's columns and the extras, in the viewer's order, each drawn — the design table's
   *  picker flags cleared, the sheet being the only picker. */
  resolve: (own: readonly IDataColumn<IListRow>[], scope: IWorkScope) => IDataColumn<IListRow>[];
  /** The *Columns* button and its sheet, over `own`. */
  button: (own: readonly IDataColumn<IListRow>[]) => React.ReactNode;
}

export function useWorkColumns(page: IWorkPage, id: string, fixed: readonly string[]): IWorkColumns {
  const [choice, save] = useColumnChoice(id);
  return useColumnsOver(page, fixed, choice, save);
}

/** A layout one team shares (SR-500): read from survey-api, changed only by whom it allows. */
export interface ISharedColumns {
  /** The layout as read — null draws the page's defaults. */
  choice: Nullable<IColumnChoice>;
  /** Saves the sheet's draft for everyone: null once saved, else what went wrong — absent where
   *  this viewer cannot. */
  save?: (made: IColumnChoice) => Promise<Nullable<string>>;
  /** Back to the page's defaults, for everyone — absent while nothing is saved. */
  reset?: () => Promise<Nullable<string>>;
  /** The id of the sentence saying why the sheet is off, where `save` is absent, and its words,
   *  said on hovering the button. */
  lockedBy?: string;
  lockedReason?: string;
  /** Said under the sheet's title. */
  description: string;
}

/** A work page's columns as its team shares them (SR-500) — `useWorkColumns`' drawing over a
 *  layout read from survey-api, its sheet a draft saved by a press rather than each change. */
export function useSharedWorkColumns(page: IWorkPage, fixed: readonly string[], shared: ISharedColumns): IWorkColumns {
  const { save, reset } = shared;
  const columns = useColumnsOver(page, fixed, shared.choice, undefined);
  return {
    extras: columns.extras,
    resolve: columns.resolve,
    button: (own) => (
      <ColumnsSheet
        catalogue={columns.offered}
        columns={columns.order(own)}
        own={own.map((column) => ({ id: column.id, label: labelOf(column) }))}
        pinned={own[0]?.id ?? null}
        description={shared.description}
        lockedBy={save ? undefined : shared.lockedBy}
        lockedReason={shared.lockedReason}
        onChange={() => undefined}
        commit={
          save
            ? {
                save: (next) => save({ order: next, known: own.map((column) => column.id) }),
                reset: reset ?? null
              }
            : undefined
        }
      />
    )
  };
}

function useColumnsOver(
  page: IWorkPage,
  fixed: readonly string[],
  choice: Nullable<IColumnChoice>,
  save: ((made: IColumnChoice) => void) | undefined
): IWorkColumns & {
  offered: IListFieldCatalogue;
  order: (own: readonly IDataColumn<IListRow>[]) => string[];
} {
  const isExtra = (code: string) => !fixed.includes(code) && page.fieldOf(code) !== undefined;
  const extras = (choice?.order ?? []).filter(isExtra);
  const offered = React.useMemo<IListFieldCatalogue>(
    () => ({
      ...page.catalogue,
      groups: page.catalogue.groups.map((group) => ({
        ...group,
        fields: group.fields.filter((field) => !fixed.includes(field.code))
      }))
    }),
    // `fixed` is the page's own, spelled where it is drawn.
    [page.catalogue, fixed.join(',')]
  );

  const order = (own: readonly IDataColumn<IListRow>[]) => orderOf(choice, own, isExtra);

  return {
    extras,
    offered,
    order,
    resolve: (own, scope) => {
      const byId = new Map(own.map((column) => [column.id, column]));
      return order(own).flatMap((code) => {
        const column = byId.get(code);
        if (column) return [{ ...column, optional: undefined, hidden: undefined }];
        return extraColumns(page, scope, [code]);
      });
    },
    button: (own) => (
      <ColumnsSheet
        catalogue={offered}
        columns={order(own)}
        own={own.map((column) => ({ id: column.id, label: labelOf(column) }))}
        pinned={own[0]?.id ?? null}
        description=""
        onChange={(next) => save?.({ order: next, known: own.map((column) => column.id) })}
      />
    )
  };
}

export interface IOwnColumns<Row> {
  /** The page's columns in the viewer's order, each drawn — the picker flags cleared. */
  resolve: (own: readonly IDataColumn<Row>[]) => IDataColumn<Row>[];
  /** The *Columns* button and its sheet over `own`, `description` said under its title. */
  button: (own: readonly IDataColumn<Row>[], description?: string) => React.ReactNode;
  /** The offered fields shown (`IOfferedFields`), in order — what the rows read asks beside the
   *  page's own. */
  extras: string[];
}

/** Fields a work-item page offers beside its own columns — the SEC ADV register's Form ADV
 *  attributes — in the sheet's groups, as a Lists catalogue spells them. */
export interface IOfferedFields<Row> {
  /** Null while it is read: a choice it holds is kept meanwhile (`offers`). */
  catalogue: Nullable<IListFieldCatalogue>;
  /** Whether a code is one of these fields, never one of the page's own — answered without the
   *  catalogue, so a choice is not dropped while it is read; a field the catalogue lost answers
   *  false. */
  offers: (code: string) => boolean;
  column: (code: string) => IDataColumn<Row>;
  /** A column's type icon in the sheet — the page's own columns' and these fields'. */
  iconOf?: (code: string) => Nullable<ColumnIcon>;
  /** The most of these fields shown at once — what the rows' read accepts; the sheet's boxes are
   *  off at it. */
  max?: number;
}

/** The fields the viewer chose under `id`, in order — what a page asks its rows read for before
 *  the sheet is drawn (SR-398). Read from the store each render, so a choice just made is read at
 *  once. */
export function useChosenCodes(id: string): string[] {
  const viewer = useViewer();
  const key = `veritant.columns.${id}.${viewer}`;
  return (chosenThisLoad.get(key) ?? readChoice(key))?.order ?? [];
}

/** A work page's own columns, the viewer's — the same sheet and store as `useWorkColumns`, for a
 *  page whose rows are not Lists' (Compliance queues), with `fields` offered beside them where the
 *  page has any. The first column is pinned. */
export function useOwnColumns<Row>(id: string, fields?: IOfferedFields<Row>): IOwnColumns<Row> {
  const [choice, save] = useColumnChoice(id);
  const offers = fields?.offers ?? (() => false);
  const order = (own: readonly IDataColumn<Row>[]) => orderOf(choice, own, offers);
  const extras = (choice?.order ?? []).filter(offers);
  const full =
    fields?.max !== undefined && extras.length >= fields.max
      ? `At most ${fields.max} added columns — remove one to add another.`
      : null;
  return {
    resolve: (own) => {
      const byId = new Map(own.map((column) => [column.id, column]));
      return order(own).flatMap((code) => {
        const column = byId.get(code);
        if (column) return [{ ...column, optional: undefined, hidden: undefined }];
        return fields ? [fields.column(code)] : [];
      });
    },
    button: (own, description = '') => (
      <ColumnsSheet
        catalogue={fields?.catalogue ?? null}
        columns={order(own)}
        own={own.map((column) => ({ id: column.id, label: labelOf(column) }))}
        pinned={own[0]?.id ?? null}
        description={description}
        listsNote={fields === undefined}
        iconOf={fields?.iconOf}
        full={full}
        onChange={(next) => save({ order: next, known: own.map((column) => column.id) })}
      />
    ),
    extras
  };
}
